import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Whether every message is within its send limits.
 *
 * MessageCreditEstimates works this out while rendering one message — segment
 * count, character set, attachment size — so it is not derivable from the form
 * values the way every other validity check is. It reports up here instead, and
 * the primary action reads the verdict.
 *
 * Without it a message the estimator has already marked invalid can still be
 * saved and sent.
 */
interface MessageValidity {
  /** Called by each message editor whenever its verdict changes. */
  report: (key: string, isValid: boolean) => void;
  /** A message dropped out of the list; forget its verdict. */
  forget: (key: string) => void;
  hasInvalid: boolean;
}

const MessageValidityContext = createContext<MessageValidity | null>(null);

export const MessageValidityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [verdicts, setVerdicts] = useState<Record<string, boolean>>({});

  const report = useCallback((key: string, isValid: boolean) => {
    setVerdicts(prev => (prev[key] === isValid ? prev : { ...prev, [key]: isValid }));
  }, []);

  const forget = useCallback((key: string) => {
    setVerdicts(prev => {
      if (!(key in prev)) return prev;
      return Object.fromEntries(Object.entries(prev).filter(([k]) => k !== key));
    });
  }, []);

  const value = useMemo(() => ({
    report,
    forget,
    // A message that has not reported yet is not held against the user.
    hasInvalid: Object.values(verdicts).some(isValid => isValid === false),
  }), [report, forget, verdicts]);

  return (
    <MessageValidityContext.Provider value={value}>{children}</MessageValidityContext.Provider>
  );
};

/** Outside a provider nothing reports and nothing is invalid, so fields stay usable. */
const INERT: MessageValidity = {
  report: () => undefined,
  forget: () => undefined,
  hasInvalid: false,
};

export const useMessageValidity = (): MessageValidity =>
  useContext(MessageValidityContext) ?? INERT;
